
// src/components/admin/page-management/section-forms/ProductCatalogForm.js
import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import axios from 'axios';

const ProductCatalogForm = ({ content, updateContent }) => {
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // useEffect to fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (content?.showAllCategories === false) {
        try {
          setLoadingCategories(true);
          const response = await axios.get('/api/categories/basic');
          setAvailableCategories(response.data);
          
          // Initialize selected categories if not already set
          if (!content.selectedCategoryIds) {
            const initialSelected = response.data.map(cat => cat._id);
            updateContent('selectedCategoryIds', initialSelected);
          }
        } catch (error) {
          console.error('Error fetching categories:', error);
        } finally {
          setLoadingCategories(false);
        }
      }
    };
    
    fetchCategories();
  }, [content?.showAllCategories]);

  // useEffect to update the category selection UI
  useEffect(() => {
    const container = document.getElementById('category-selection-container');
    if (container && availableCategories.length > 0 && content?.showAllCategories === false) {
      container.innerHTML = '';
      
      const div = document.createElement('div');
      div.className = 'max-h-60 overflow-y-auto border rounded p-2';
      
      availableCategories.forEach(category => {
        const isSelected = content.selectedCategoryIds?.includes(category._id) || false;
        
        const checkboxDiv = document.createElement('div');
        checkboxDiv.className = 'flex items-center mb-2';
        
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'mr-2';
        input.checked = isSelected;
        input.onchange = () => {
          const currentSelected = [...(content.selectedCategoryIds || [])];
          if (input.checked && !currentSelected.includes(category._id)) {
            updateContent('selectedCategoryIds', [...currentSelected, category._id]);
          } else if (!input.checked && currentSelected.includes(category._id)) {
            updateContent('selectedCategoryIds', currentSelected.filter(id => id !== category._id));
          }
        };
        
        const label = document.createElement('label');
        label.className = 'text-sm';
        label.textContent = category.name;
        
        checkboxDiv.appendChild(input);
        checkboxDiv.appendChild(label);
        div.appendChild(checkboxDiv);
      });
      
      container.innerHTML = '';
      container.appendChild(div);
    }
  }, [availableCategories, content?.selectedCategoryIds, content?.showAllCategories]);

  const handleColorChange = (key, value) => {
    // Ensure color values include the # if missing
    if (value && !value.startsWith('#') && value.length > 0) {
      value = '#' + value;
    }
    updateContent(key, value);
  };
  

  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Product Catalog Section</h3>
      
      {/* Section Header */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Section Header</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Section Title</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={content?.title || 'Products'}
            onChange={(e) => updateContent('title', e.target.value)}
            placeholder="e.g., Our Products, Product Catalog, etc."
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Section Description</label>
          <textarea
            className="w-full p-2 border rounded"
            rows="2"
            value={content?.description || 'Browse our selection of premium packaging solutions'}
            onChange={(e) => updateContent('description', e.target.value)}
            placeholder="Brief description of the product section"
          ></textarea>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Background Color</label>
          <input
            type="color"
            className="w-full p-1 border rounded h-10"
            value={content?.backgroundColor || '#F9FAFB'}
            onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
          />
        </div>
      </div>
      
      {/* Search Configuration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Search Configuration</h4>
        
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="showSearch"
            className="mr-2"
            checked={content?.showSearch !== false}
            onChange={(e) => updateContent('showSearch', e.target.checked)}
          />
          <label htmlFor="showSearch" className="text-sm font-medium">Show Search</label>
        </div>
        
        {content?.showSearch !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Search Placeholder</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={content?.searchPlaceholder || 'Search between all categories...'}
              onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
            />
          </div>
        )}
      </div>
      
      {/* Category Slider Configuration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Category Slider Configuration</h4>
        
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="showCategorySlider"
            className="mr-2"
            checked={content?.showCategorySlider !== false}
            onChange={(e) => updateContent('showCategorySlider', e.target.checked)}
          />
          <label htmlFor="showCategorySlider" className="text-sm font-medium">Show Category Slider</label>
        </div>
        
        {content?.showCategorySlider !== false && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Category Item Size</label>
              <select
                className="w-full p-2 border rounded"
                value={content?.categoryItemSize || 'medium'}
                onChange={(e) => updateContent('categoryItemSize', e.target.value)}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Category Border Color</label>
              <input
                type="color"
                className="w-full p-1 border rounded h-10"
                value={content?.categoryBorderColor || '#3B82F6'}
                onChange={(e) => handleColorChange('categoryBorderColor', e.target.value)}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Selected Category Border Color</label>
              <input
                type="color"
                className="w-full p-1 border rounded h-10"
                value={content?.selectedCategoryBorderColor || '#F59E0B'}
                onChange={(e) => handleColorChange('selectedCategoryBorderColor', e.target.value)}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Initial Selected Category Slug (optional)</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={content?.initialCategorySlug || ''}
                onChange={(e) => updateContent('initialCategorySlug', e.target.value)}
                placeholder="e.g., shopping-bags"
              />
              <p className="text-xs text-gray-500 mt-1">
                If set, this category will be selected when the page loads. Leave empty to use the URL or no initial selection.
              </p>
            </div>
          </>
        )}
      </div>


      {/* Category Display Configuration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Category Display Configuration</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Category Display Mode</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.categoryDisplayMode || 'slider'}
            onChange={(e) => updateContent('categoryDisplayMode', e.target.value)}
          >
            <option value="slider">Slider (Horizontal Scroll)</option>
            <option value="grid">Grid Layout</option>
            <option value="fullWidth">Full Width (One at a Time)</option>
            <option value="banner">Banner Style</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            "Full Width" shows one category at a time, with next/prev navigation
          </p>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Category Shape</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.categoryShape || 'circle'}
            onChange={(e) => updateContent('categoryShape', e.target.value)}
          >
            <option value="circle">Circle</option>
            <option value="rectangle">Rectangle</option>
            <option value="square">Square</option>
            <option value="pill">Pill Shape</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Category Text Position</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.categoryTextPosition || 'below'}
            onChange={(e) => updateContent('categoryTextPosition', e.target.value)}
          >
            <option value="below">Below Image</option>
            <option value="overlay">Text Overlay</option>
            <option value="none">No Text</option>
          </select>
        </div>
        
        {content?.categoryTextPosition === 'overlay' && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Overlay Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                className="w-full"
                value={content?.categoryOverlayOpacity || '0.7'}
                onChange={(e) => updateContent('categoryOverlayOpacity', e.target.value)}
              />
              <div className="flex justify-between text-xs">
                <span>Transparent</span>
                <span>Opaque</span>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Text Color</label>
              <input
                type="color"
                className="w-full p-1 border rounded h-10"
                value={content?.categoryTextColor || '#ffffff'}
                onChange={(e) => handleColorChange('categoryTextColor', e.target.value)}
              />
            </div>
          </>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Hover Effect</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.categoryHoverEffect || 'zoom'}
            onChange={(e) => updateContent('categoryHoverEffect', e.target.value)}
          >
            <option value="zoom">Zoom</option>
            <option value="flip">Flip</option>
            <option value="fade">Fade</option>
            <option value="glow">Glow</option>
            <option value="none">None</option>
          </select>
        </div>
      </div>

      {/* Category Selection */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Category Selection</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              id="showAllCategories"
              className="mr-2"
              checked={content?.showAllCategories !== false}
              onChange={(e) => updateContent('showAllCategories', e.target.checked)}
            />
            <label htmlFor="showAllCategories" className="text-sm font-medium">Show All Categories</label>
          </label>
        </div>
        
        {content?.showAllCategories === false && (
          <div id="category-selection-container" className="mt-4">
            <label className="block text-sm font-medium mb-2">Select Specific Categories</label>
            <div className="max-h-60 overflow-y-auto border rounded p-2">
              <div className="text-sm text-gray-500 mb-2">
                Loading categories... If no categories appear, save the page first and edit again.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Price Display Configuration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Price Display Configuration</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Display Price Type</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.priceDisplayType || 'regular'}
            onChange={(e) => updateContent('priceDisplayType', e.target.value)}
          >
            <option value="regular">Regular Price</option>
            <option value="tier">Pricing Tier (Last Bound)</option>
            <option value="wholesale">Wholesale Price</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Select which price to display for products when "Show Price" is enabled
          </p>
        </div>

        {/* Quantity Selection for Add to Cart */}
        <div className="mt-6">
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="allowQuantitySelection"
              className="mr-2"
              checked={content?.allowQuantitySelection === true}
              onChange={(e) => updateContent('allowQuantitySelection', e.target.checked)}
            />
            <label htmlFor="allowQuantitySelection" className="text-sm font-medium">
              Allow Quantity Selection when Adding to Cart
            </label>
          </div>
          
          {content?.allowQuantitySelection && (
            <div className="mb-4 ml-6">
              <label className="block text-sm font-medium mb-1">Default Quantity</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                min="1"
                value={content?.defaultQuantity || 
                  (content?.priceDisplayType === 'wholesale' ? 50 : 1)}
                onChange={(e) => updateContent('defaultQuantity', 
                  parseInt(e.target.value, 10) || 
                  (content?.priceDisplayType === 'wholesale' ? 50 : 1)
                )}
              />
              <p className="text-xs text-gray-500 mt-1">
                {content?.priceDisplayType === 'regular' && 
                  "For regular price, quantity can be incremented by 1 (default is 1)"}
                {content?.priceDisplayType === 'tier' && 
                  "For tier pricing, quantity will follow tier bounds"}
                {content?.priceDisplayType === 'wholesale' && 
                  "For wholesale price, quantity can be incremented by 1 (default is 50)"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Product Display Configuration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Product Information Display</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Information Fields</label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showName"
                className="mr-2"
                checked={content?.productDisplayFields?.showName !== false}
                onChange={(e) => updateContent('productDisplayFields', {
                  ...(content?.productDisplayFields || {}),
                  showName: e.target.checked
                })}
              />
              <label htmlFor="showName" className="text-sm">Show Product Name</label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showPrice"
                className="mr-2"
                checked={content?.productDisplayFields?.showPrice !== false}
                onChange={(e) => updateContent('productDisplayFields', {
                  ...(content?.productDisplayFields || {}),
                  showPrice: e.target.checked
                })}
              />
              <label htmlFor="showPrice" className="text-sm">Show Price</label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showDescription"
                className="mr-2"
                checked={content?.productDisplayFields?.showDescription === true}
                onChange={(e) => updateContent('productDisplayFields', {
                  ...(content?.productDisplayFields || {}),
                  showDescription: e.target.checked
                })}
              />
              <label htmlFor="showDescription" className="text-sm">Show Description</label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showNote"
                className="mr-2"
                checked={content?.productDisplayFields?.showNote === true}
                onChange={(e) => updateContent('productDisplayFields', {
                  ...(content?.productDisplayFields || {}),
                  showNote: e.target.checked
                })}
              />
              <label htmlFor="showNote" className="text-sm">Show Note</label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showRating"
                className="mr-2"
                checked={content?.productDisplayFields?.showRating === true}
                onChange={(e) => updateContent('productDisplayFields', {
                  ...(content?.productDisplayFields || {}),
                  showRating: e.target.checked
                })}
              />
              <label htmlFor="showRating" className="text-sm">Show Rating</label>
            </div>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Product Info Position</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productInfoPosition || 'below'}
            onChange={(e) => updateContent('productInfoPosition', e.target.value)}
          >
            <option value="below">Below Image</option>
            <option value="right">Right of Image</option>
            <option value="left">Left of Image</option>
            <option value="overlay">Overlay on Image</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Text Alignment</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productTextAlignment || 'left'}
            onChange={(e) => updateContent('productTextAlignment', e.target.value)}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Product Name Max Lines</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productNameLines || 2}
            onChange={(e) => updateContent('productNameLines', parseInt(e.target.value, 10))}
          >
            <option value={1}>1 Line</option>
            <option value={2}>2 Lines</option>
            <option value={3}>3 Lines</option>
            <option value={0}>No Limit</option>
          </select>
        </div>
        
        {content?.productDisplayFields?.showDescription && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Description Max Lines</label>
            <select
              className="w-full p-2 border rounded"
              value={content?.productDescriptionLines || 3}
              onChange={(e) => updateContent('productDescriptionLines', parseInt(e.target.value, 10))}
            >
              <option value={1}>1 Line</option>
              <option value={2}>2 Lines</option>
              <option value={3}>3 Lines</option>
              <option value={4}>4 Lines</option>
              <option value={5}>5 Lines</option>
              <option value={0}>No Limit</option>
            </select>
          </div>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productImageAspectRatio || 'square'}
            onChange={(e) => updateContent('productImageAspectRatio', e.target.value)}
          >
            <option value="square">Square (1:1)</option>
            <option value="portrait">Portrait (3:4)</option>
            <option value="landscape">Landscape (4:3)</option>
            <option value="original">Original (As uploaded)</option>
          </select>
        </div>
      </div>

      {/* Product Interaction Configuration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Product Click Behavior</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">When a product is clicked:</label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="radio"
                id="productClickNavigate"
                name="productClickBehavior"
                className="mr-2"
                checked={content?.productClickBehavior !== 'popup'}
                onChange={() => updateContent('productClickBehavior', 'navigate')}
              />
              <label htmlFor="productClickNavigate" className="text-sm">Navigate to product detail page</label>
            </div>
            
            <div className="flex items-center">
              <input
                type="radio"
                id="productClickPopup"
                name="productClickBehavior"
                className="mr-2"
                checked={content?.productClickBehavior === 'popup'}
                onChange={() => updateContent('productClickBehavior', 'popup')}
              />
              <label htmlFor="productClickPopup" className="text-sm">Show product details in popup</label>
            </div>
          </div>
        </div>
        
        {content?.productClickBehavior === 'popup' && (
          <div className="border-t pt-4 mt-2">
            <h5 className="font-medium text-sm mb-3">Popup Settings</h5>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Information to Display</label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showGallery"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showGallery !== false}
                    onChange={(e) => updateContent('productPopupSettings', {
                      ...(content?.productPopupSettings || {}),
                      showGallery: e.target.checked
                    })}
                  />
                  <label htmlFor="showGallery" className="text-sm">Image Gallery</label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showDescription"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showDescription !== false}
                    onChange={(e) => updateContent('productPopupSettings', {
                      ...(content?.productPopupSettings || {}),
                      showDescription: e.target.checked
                    })}
                  />
                  <label htmlFor="showDescription" className="text-sm">Description</label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPrice"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showPrice !== false}
                    onChange={(e) => updateContent('productPopupSettings', {
                      ...(content?.productPopupSettings || {}),
                      showPrice: e.target.checked
                    })}
                  />
                  <label htmlFor="showPrice" className="text-sm">Price Information</label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showNote"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showNote !== false}
                    onChange={(e) => updateContent('productPopupSettings', {
                      ...(content?.productPopupSettings || {}),
                      showNote: e.target.checked
                    })}
                  />
                  <label htmlFor="showNote" className="text-sm">Product Note</label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showAttributes"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showAttributes !== false}
                    onChange={(e) => updateContent('productPopupSettings', {
                      ...(content?.productPopupSettings || {}),
                      showAttributes: e.target.checked
                    })}
                  />
                  <label htmlFor="showAttributes" className="text-sm">Product Attributes</label>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Action Buttons</label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showAddToCart"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showAddToCart !== false}
                    onChange={(e) => updateContent('productPopupSettings', {
                      ...(content?.productPopupSettings || {}),
                      showAddToCart: e.target.checked
                    })}
                  />
                  <label htmlFor="showAddToCart" className="text-sm">Add to Cart Button</label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showCustomizeButton"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showCustomizeButton !== false}
                    onChange={(e) => updateContent('productPopupSettings', {
                      ...(content?.productPopupSettings || {}),
                      showCustomizeButton: e.target.checked
                    })}
                  />
                  <label htmlFor="showCustomizeButton" className="text-sm">Customize Product Button</label>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Popup Size</label>
              <select
                className="w-full p-2 border rounded"
                value={content?.productPopupSettings?.popupSize || 'medium'}
                onChange={(e) => updateContent('productPopupSettings', {
                  ...(content?.productPopupSettings || {}),
                  popupSize: e.target.value
                })}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="fullscreen">Full Screen</option>
              </select>
            </div>
          </div>
        )}
      </div>

      
      {/* Product Grid Configuration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-base mb-3">Product Grid Configuration</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Products Per Row</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productsPerRow || 3}
            onChange={(e) => updateContent('productsPerRow', parseInt(e.target.value, 10))}
          >
            <option value={1}>1 Product</option>
            <option value={2}>2 Products</option>
            <option value={3}>3 Products</option>
            <option value={4}>4 Products</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Product Card Style</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productCardStyle || 'shadow'}
            onChange={(e) => updateContent('productCardStyle', e.target.value)}
          >
            <option value="shadow">Shadow</option>
            <option value="outline">Outline</option>
            <option value="flat">Flat</option>
          </select>
        </div>
        
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="showQuickAction"
            className="mr-2"
            checked={content?.showQuickAction !== false}
            onChange={(e) => updateContent('showQuickAction', e.target.checked)}
          />
          <label htmlFor="showQuickAction" className="text-sm font-medium">Show Quick Action Button</label>
        </div>
        
        {content?.showQuickAction !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Quick Action Icon</label>
            <select
              className="w-full p-2 border rounded"
              value={content?.quickActionIcon || 'customize'}
              onChange={(e) => updateContent('quickActionIcon', e.target.value)}
            >
              <option value="customize">Customize (Pencil)</option>
              <option value="cart">Add to Cart</option>
            </select>
          </div>
        )}
      </div>
      
      {/* API Configuration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-start mb-3">
          <h4 className="font-medium text-base">API Configuration</h4>
          <div className="rounded-full bg-blue-100 p-1">
            <Info size={16} className="text-blue-600" />
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          These settings are for advanced users. Most users should leave these at their default values.
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Category API Path</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={content?.categoryApiPath || '/api/categories'}
            onChange={(e) => updateContent('categoryApiPath', e.target.value)}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Product API Path</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={content?.productApiPath || '/api/products/basic'}
            onChange={(e) => updateContent('productApiPath', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCatalogForm;


















// // in case of slow speed, roll back to this version
// //working before category selection with high speed
// src/components/admin/page-management/section-forms/ProductCatalogForm.js
// import React from 'react';
// import { Info } from 'lucide-react';

// const ProductCatalogForm = ({ content, updateContent }) => {
//   const handleColorChange = (key, value) => {
//     // Ensure color values include the # if missing
//     if (value && !value.startsWith('#') && value.length > 0) {
//       value = '#' + value;
//     }
//     updateContent(key, value);
//   };

//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Product Catalog Section</h3>
      
//       {/* Section Header */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Section Header</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Title</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.title || 'Products'}
//             onChange={(e) => updateContent('title', e.target.value)}
//             placeholder="e.g., Our Products, Product Catalog, etc."
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Description</label>
//           <textarea
//             className="w-full p-2 border rounded"
//             rows="2"
//             value={content?.description || 'Browse our selection of premium packaging solutions'}
//             onChange={(e) => updateContent('description', e.target.value)}
//             placeholder="Brief description of the product section"
//           ></textarea>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <input
//             type="color"
//             className="w-full p-1 border rounded h-10"
//             value={content?.backgroundColor || '#F9FAFB'}
//             onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
//           />
//         </div>
//       </div>
      
//       {/* Search Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Search Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showSearch"
//             className="mr-2"
//             checked={content?.showSearch !== false}
//             onChange={(e) => updateContent('showSearch', e.target.checked)}
//           />
//           <label htmlFor="showSearch" className="text-sm font-medium">Show Search</label>
//         </div>
        
//         {content?.showSearch !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Search Placeholder</label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.searchPlaceholder || 'Search between all categories...'}
//               onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Category Slider Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Slider Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showCategorySlider"
//             className="mr-2"
//             checked={content?.showCategorySlider !== false}
//             onChange={(e) => updateContent('showCategorySlider', e.target.checked)}
//           />
//           <label htmlFor="showCategorySlider" className="text-sm font-medium">Show Category Slider</label>
//         </div>
        
//         {content?.showCategorySlider !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Item Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.categoryItemSize || 'medium'}
//                 onChange={(e) => updateContent('categoryItemSize', e.target.value)}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryBorderColor || '#3B82F6'}
//                 onChange={(e) => handleColorChange('categoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Selected Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.selectedCategoryBorderColor || '#F59E0B'}
//                 onChange={(e) => handleColorChange('selectedCategoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Initial Selected Category Slug (optional)</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={content?.initialCategorySlug || ''}
//                 onChange={(e) => updateContent('initialCategorySlug', e.target.value)}
//                 placeholder="e.g., shopping-bags"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 If set, this category will be selected when the page loads. Leave empty to use the URL or no initial selection.
//               </p>
//             </div>
//           </>
//         )}
//       </div>


//       {/* Category Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Display Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Display Mode</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryDisplayMode || 'slider'}
//             onChange={(e) => updateContent('categoryDisplayMode', e.target.value)}
//           >
//             <option value="slider">Slider (Horizontal Scroll)</option>
//             <option value="grid">Grid Layout</option>
//             <option value="fullWidth">Full Width (One at a Time)</option>
//             <option value="banner">Banner Style</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             "Full Width" shows one category at a time, with next/prev navigation
//           </p>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Shape</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryShape || 'circle'}
//             onChange={(e) => updateContent('categoryShape', e.target.value)}
//           >
//             <option value="circle">Circle</option>
//             <option value="rectangle">Rectangle</option>
//             <option value="square">Square</option>
//             <option value="pill">Pill Shape</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Text Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryTextPosition || 'below'}
//             onChange={(e) => updateContent('categoryTextPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="overlay">Text Overlay</option>
//             <option value="none">No Text</option>
//           </select>
//         </div>
        
//         {content?.categoryTextPosition === 'overlay' && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Overlay Opacity</label>
//               <input
//                 type="range"
//                 min="0"
//                 max="1"
//                 step="0.1"
//                 className="w-full"
//                 value={content?.categoryOverlayOpacity || '0.7'}
//                 onChange={(e) => updateContent('categoryOverlayOpacity', e.target.value)}
//               />
//               <div className="flex justify-between text-xs">
//                 <span>Transparent</span>
//                 <span>Opaque</span>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Text Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryTextColor || '#ffffff'}
//                 onChange={(e) => handleColorChange('categoryTextColor', e.target.value)}
//               />
//             </div>
//           </>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Hover Effect</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryHoverEffect || 'zoom'}
//             onChange={(e) => updateContent('categoryHoverEffect', e.target.value)}
//           >
//             <option value="zoom">Zoom</option>
//             <option value="flip">Flip</option>
//             <option value="fade">Fade</option>
//             <option value="glow">Glow</option>
//             <option value="none">None</option>
//           </select>
//         </div>
//       </div>

//       {/* Price Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Price Display Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Display Price Type</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.priceDisplayType || 'regular'}
//             onChange={(e) => updateContent('priceDisplayType', e.target.value)}
//           >
//             <option value="regular">Regular Price</option>
//             <option value="tier">Pricing Tier (Last Bound)</option>
//             <option value="wholesale">Wholesale Price</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             Select which price to display for products when "Show Price" is enabled
//           </p>
//         </div>

//         {/* Quantity Selection for Add to Cart */}
//         <div className="mt-6">
//           <div className="flex items-center mb-4">
//             <input
//               type="checkbox"
//               id="allowQuantitySelection"
//               className="mr-2"
//               checked={content?.allowQuantitySelection === true}
//               onChange={(e) => updateContent('allowQuantitySelection', e.target.checked)}
//             />
//             <label htmlFor="allowQuantitySelection" className="text-sm font-medium">
//               Allow Quantity Selection when Adding to Cart
//             </label>
//           </div>
          
//           {content?.allowQuantitySelection && (
//             <div className="mb-4 ml-6">
//               <label className="block text-sm font-medium mb-1">Default Quantity</label>
//               <input
//                 type="number"
//                 className="w-full p-2 border rounded"
//                 min="1"
//                 value={content?.defaultQuantity || 
//                   (content?.priceDisplayType === 'wholesale' ? 50 : 1)}
//                 onChange={(e) => updateContent('defaultQuantity', 
//                   parseInt(e.target.value, 10) || 
//                   (content?.priceDisplayType === 'wholesale' ? 50 : 1)
//                 )}
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 {content?.priceDisplayType === 'regular' && 
//                   "For regular price, quantity can be incremented by 1 (default is 1)"}
//                 {content?.priceDisplayType === 'tier' && 
//                   "For tier pricing, quantity will follow tier bounds"}
//                 {content?.priceDisplayType === 'wholesale' && 
//                   "For wholesale price, quantity can be incremented by 1 (default is 50)"}
//               </p>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Product Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Information Display</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-2">Information Fields</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showName"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showName !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showName: e.target.checked
//                 })}
//               />
//               <label htmlFor="showName" className="text-sm">Show Product Name</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showPrice"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showPrice !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showPrice: e.target.checked
//                 })}
//               />
//               <label htmlFor="showPrice" className="text-sm">Show Price</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showDescription"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showDescription === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showDescription: e.target.checked
//                 })}
//               />
//               <label htmlFor="showDescription" className="text-sm">Show Description</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showNote"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showNote === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showNote: e.target.checked
//                 })}
//               />
//               <label htmlFor="showNote" className="text-sm">Show Note</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showRating"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showRating === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showRating: e.target.checked
//                 })}
//               />
//               <label htmlFor="showRating" className="text-sm">Show Rating</label>
//             </div>
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Info Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productInfoPosition || 'below'}
//             onChange={(e) => updateContent('productInfoPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="right">Right of Image</option>
//             <option value="left">Left of Image</option>
//             <option value="overlay">Overlay on Image</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Text Alignment</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productTextAlignment || 'left'}
//             onChange={(e) => updateContent('productTextAlignment', e.target.value)}
//           >
//             <option value="left">Left</option>
//             <option value="center">Center</option>
//             <option value="right">Right</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Name Max Lines</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productNameLines || 2}
//             onChange={(e) => updateContent('productNameLines', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Line</option>
//             <option value={2}>2 Lines</option>
//             <option value={3}>3 Lines</option>
//             <option value={0}>No Limit</option>
//           </select>
//         </div>
        
//         {content?.productDisplayFields?.showDescription && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Description Max Lines</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.productDescriptionLines || 3}
//               onChange={(e) => updateContent('productDescriptionLines', parseInt(e.target.value, 10))}
//             >
//               <option value={1}>1 Line</option>
//               <option value={2}>2 Lines</option>
//               <option value={3}>3 Lines</option>
//               <option value={4}>4 Lines</option>
//               <option value={5}>5 Lines</option>
//               <option value={0}>No Limit</option>
//             </select>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productImageAspectRatio || 'square'}
//             onChange={(e) => updateContent('productImageAspectRatio', e.target.value)}
//           >
//             <option value="square">Square (1:1)</option>
//             <option value="portrait">Portrait (3:4)</option>
//             <option value="landscape">Landscape (4:3)</option>
//             <option value="original">Original (As uploaded)</option>
//           </select>
//         </div>
//       </div>

//       {/* Product Interaction Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Click Behavior</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">When a product is clicked:</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickNavigate"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior !== 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'navigate')}
//               />
//               <label htmlFor="productClickNavigate" className="text-sm">Navigate to product detail page</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickPopup"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior === 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'popup')}
//               />
//               <label htmlFor="productClickPopup" className="text-sm">Show product details in popup</label>
//             </div>
//           </div>
//         </div>
        
//         {content?.productClickBehavior === 'popup' && (
//           <div className="border-t pt-4 mt-2">
//             <h5 className="font-medium text-sm mb-3">Popup Settings</h5>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Information to Display</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showGallery"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showGallery !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showGallery: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showGallery" className="text-sm">Image Gallery</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showDescription"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showDescription !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showDescription: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showDescription" className="text-sm">Description</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showPrice"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showPrice !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showPrice: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showPrice" className="text-sm">Price Information</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showNote"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showNote !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showNote: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showNote" className="text-sm">Product Note</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAttributes"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAttributes !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAttributes: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAttributes" className="text-sm">Product Attributes</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Action Buttons</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAddToCart"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAddToCart !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAddToCart: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAddToCart" className="text-sm">Add to Cart Button</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showCustomizeButton"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showCustomizeButton !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showCustomizeButton: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showCustomizeButton" className="text-sm">Customize Product Button</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Popup Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.productPopupSettings?.popupSize || 'medium'}
//                 onChange={(e) => updateContent('productPopupSettings', {
//                   ...(content?.productPopupSettings || {}),
//                   popupSize: e.target.value
//                 })}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//                 <option value="fullscreen">Full Screen</option>
//               </select>
//             </div>
//           </div>
//         )}
//       </div>

      
//       {/* Product Grid Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Grid Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Products Per Row</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productsPerRow || 3}
//             onChange={(e) => updateContent('productsPerRow', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Product</option>
//             <option value={2}>2 Products</option>
//             <option value={3}>3 Products</option>
//             <option value={4}>4 Products</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Card Style</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productCardStyle || 'shadow'}
//             onChange={(e) => updateContent('productCardStyle', e.target.value)}
//           >
//             <option value="shadow">Shadow</option>
//             <option value="outline">Outline</option>
//             <option value="flat">Flat</option>
//           </select>
//         </div>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showQuickAction"
//             className="mr-2"
//             checked={content?.showQuickAction !== false}
//             onChange={(e) => updateContent('showQuickAction', e.target.checked)}
//           />
//           <label htmlFor="showQuickAction" className="text-sm font-medium">Show Quick Action Button</label>
//         </div>
        
//         {content?.showQuickAction !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Quick Action Icon</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.quickActionIcon || 'customize'}
//               onChange={(e) => updateContent('quickActionIcon', e.target.value)}
//             >
//               <option value="customize">Customize (Pencil)</option>
//               <option value="cart">Add to Cart</option>
//             </select>
//           </div>
//         )}
//       </div>
      
//       {/* API Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <div className="flex justify-between items-start mb-3">
//           <h4 className="font-medium text-base">API Configuration</h4>
//           <div className="rounded-full bg-blue-100 p-1">
//             <Info size={16} className="text-blue-600" />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.categoryApiPath || '/api/categories'}
//             onChange={(e) => updateContent('categoryApiPath', e.target.value)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.productApiPath || '/api/products/basic'}
//             onChange={(e) => updateContent('productApiPath', e.target.value)}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductCatalogForm;










// // do not remove this version
// // working with price tiering
// // src/components/admin/page-management/section-forms/ProductCatalogForm.js
// import React from 'react';
// import { Info } from 'lucide-react';

// const ProductCatalogForm = ({ content, updateContent }) => {
//   const handleColorChange = (key, value) => {
//     // Ensure color values include the # if missing
//     if (value && !value.startsWith('#') && value.length > 0) {
//       value = '#' + value;
//     }
//     updateContent(key, value);
//   };

//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Product Catalog Section</h3>
      
//       {/* Section Header */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Section Header</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Title</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.title || 'Products'}
//             onChange={(e) => updateContent('title', e.target.value)}
//             placeholder="e.g., Our Products, Product Catalog, etc."
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Section Description</label>
//           <textarea
//             className="w-full p-2 border rounded"
//             rows="2"
//             value={content?.description || 'Browse our selection of premium packaging solutions'}
//             onChange={(e) => updateContent('description', e.target.value)}
//             placeholder="Brief description of the product section"
//           ></textarea>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <input
//             type="color"
//             className="w-full p-1 border rounded h-10"
//             value={content?.backgroundColor || '#F9FAFB'}
//             onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
//           />
//         </div>
//       </div>
      
//       {/* Search Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Search Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showSearch"
//             className="mr-2"
//             checked={content?.showSearch !== false}
//             onChange={(e) => updateContent('showSearch', e.target.checked)}
//           />
//           <label htmlFor="showSearch" className="text-sm font-medium">Show Search</label>
//         </div>
        
//         {content?.showSearch !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Search Placeholder</label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.searchPlaceholder || 'Search between all categories...'}
//               onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Category Slider Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Slider Configuration</h4>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showCategorySlider"
//             className="mr-2"
//             checked={content?.showCategorySlider !== false}
//             onChange={(e) => updateContent('showCategorySlider', e.target.checked)}
//           />
//           <label htmlFor="showCategorySlider" className="text-sm font-medium">Show Category Slider</label>
//         </div>
        
//         {content?.showCategorySlider !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Item Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.categoryItemSize || 'medium'}
//                 onChange={(e) => updateContent('categoryItemSize', e.target.value)}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryBorderColor || '#3B82F6'}
//                 onChange={(e) => handleColorChange('categoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Selected Category Border Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.selectedCategoryBorderColor || '#F59E0B'}
//                 onChange={(e) => handleColorChange('selectedCategoryBorderColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Initial Selected Category Slug (optional)</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={content?.initialCategorySlug || ''}
//                 onChange={(e) => updateContent('initialCategorySlug', e.target.value)}
//                 placeholder="e.g., shopping-bags"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 If set, this category will be selected when the page loads. Leave empty to use the URL or no initial selection.
//               </p>
//             </div>
//           </>
//         )}
//       </div>


//       {/* Category Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Category Display Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Display Mode</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryDisplayMode || 'slider'}
//             onChange={(e) => updateContent('categoryDisplayMode', e.target.value)}
//           >
//             <option value="slider">Slider (Horizontal Scroll)</option>
//             <option value="grid">Grid Layout</option>
//             <option value="fullWidth">Full Width (One at a Time)</option>
//             <option value="banner">Banner Style</option>
//           </select>
//           <p className="text-xs text-gray-500 mt-1">
//             "Full Width" shows one category at a time, with next/prev navigation
//           </p>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Shape</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryShape || 'circle'}
//             onChange={(e) => updateContent('categoryShape', e.target.value)}
//           >
//             <option value="circle">Circle</option>
//             <option value="rectangle">Rectangle</option>
//             <option value="square">Square</option>
//             <option value="pill">Pill Shape</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category Text Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryTextPosition || 'below'}
//             onChange={(e) => updateContent('categoryTextPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="overlay">Text Overlay</option>
//             <option value="none">No Text</option>
//           </select>
//         </div>
        
//         {content?.categoryTextPosition === 'overlay' && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Overlay Opacity</label>
//               <input
//                 type="range"
//                 min="0"
//                 max="1"
//                 step="0.1"
//                 className="w-full"
//                 value={content?.categoryOverlayOpacity || '0.7'}
//                 onChange={(e) => updateContent('categoryOverlayOpacity', e.target.value)}
//               />
//               <div className="flex justify-between text-xs">
//                 <span>Transparent</span>
//                 <span>Opaque</span>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Text Color</label>
//               <input
//                 type="color"
//                 className="w-full p-1 border rounded h-10"
//                 value={content?.categoryTextColor || '#ffffff'}
//                 onChange={(e) => handleColorChange('categoryTextColor', e.target.value)}
//               />
//             </div>
//           </>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Hover Effect</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.categoryHoverEffect || 'zoom'}
//             onChange={(e) => updateContent('categoryHoverEffect', e.target.value)}
//           >
//             <option value="zoom">Zoom</option>
//             <option value="flip">Flip</option>
//             <option value="fade">Fade</option>
//             <option value="glow">Glow</option>
//             <option value="none">None</option>
//           </select>
//         </div>
//       </div>

//       {/* Product Display Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Information Display</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-2">Information Fields</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showName"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showName !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showName: e.target.checked
//                 })}
//               />
//               <label htmlFor="showName" className="text-sm">Show Product Name</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showPrice"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showPrice !== false}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showPrice: e.target.checked
//                 })}
//               />
//               <label htmlFor="showPrice" className="text-sm">Show Price</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showDescription"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showDescription === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showDescription: e.target.checked
//                 })}
//               />
//               <label htmlFor="showDescription" className="text-sm">Show Description</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showNote"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showNote === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showNote: e.target.checked
//                 })}
//               />
//               <label htmlFor="showNote" className="text-sm">Show Note</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="showRating"
//                 className="mr-2"
//                 checked={content?.productDisplayFields?.showRating === true}
//                 onChange={(e) => updateContent('productDisplayFields', {
//                   ...(content?.productDisplayFields || {}),
//                   showRating: e.target.checked
//                 })}
//               />
//               <label htmlFor="showRating" className="text-sm">Show Rating</label>
//             </div>
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Info Position</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productInfoPosition || 'below'}
//             onChange={(e) => updateContent('productInfoPosition', e.target.value)}
//           >
//             <option value="below">Below Image</option>
//             <option value="right">Right of Image</option>
//             <option value="left">Left of Image</option>
//             <option value="overlay">Overlay on Image</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Text Alignment</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productTextAlignment || 'left'}
//             onChange={(e) => updateContent('productTextAlignment', e.target.value)}
//           >
//             <option value="left">Left</option>
//             <option value="center">Center</option>
//             <option value="right">Right</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Name Max Lines</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productNameLines || 2}
//             onChange={(e) => updateContent('productNameLines', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Line</option>
//             <option value={2}>2 Lines</option>
//             <option value={3}>3 Lines</option>
//             <option value={0}>No Limit</option>
//           </select>
//         </div>
        
//         {content?.productDisplayFields?.showDescription && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Description Max Lines</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.productDescriptionLines || 3}
//               onChange={(e) => updateContent('productDescriptionLines', parseInt(e.target.value, 10))}
//             >
//               <option value={1}>1 Line</option>
//               <option value={2}>2 Lines</option>
//               <option value={3}>3 Lines</option>
//               <option value={4}>4 Lines</option>
//               <option value={5}>5 Lines</option>
//               <option value={0}>No Limit</option>
//             </select>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productImageAspectRatio || 'square'}
//             onChange={(e) => updateContent('productImageAspectRatio', e.target.value)}
//           >
//             <option value="square">Square (1:1)</option>
//             <option value="portrait">Portrait (3:4)</option>
//             <option value="landscape">Landscape (4:3)</option>
//             <option value="original">Original (As uploaded)</option>
//           </select>
//         </div>
//       </div>

//       {/* Product Interaction Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Click Behavior</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">When a product is clicked:</label>
//           <div className="space-y-2">
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickNavigate"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior !== 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'navigate')}
//               />
//               <label htmlFor="productClickNavigate" className="text-sm">Navigate to product detail page</label>
//             </div>
            
//             <div className="flex items-center">
//               <input
//                 type="radio"
//                 id="productClickPopup"
//                 name="productClickBehavior"
//                 className="mr-2"
//                 checked={content?.productClickBehavior === 'popup'}
//                 onChange={() => updateContent('productClickBehavior', 'popup')}
//               />
//               <label htmlFor="productClickPopup" className="text-sm">Show product details in popup</label>
//             </div>
//           </div>
//         </div>
        
//         {content?.productClickBehavior === 'popup' && (
//           <div className="border-t pt-4 mt-2">
//             <h5 className="font-medium text-sm mb-3">Popup Settings</h5>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Information to Display</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showGallery"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showGallery !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showGallery: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showGallery" className="text-sm">Image Gallery</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showDescription"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showDescription !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showDescription: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showDescription" className="text-sm">Description</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showPrice"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showPrice !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showPrice: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showPrice" className="text-sm">Price Information</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showNote"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showNote !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showNote: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showNote" className="text-sm">Product Note</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAttributes"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAttributes !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAttributes: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAttributes" className="text-sm">Product Attributes</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-2">Action Buttons</label>
//               <div className="space-y-2">
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showAddToCart"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showAddToCart !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showAddToCart: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showAddToCart" className="text-sm">Add to Cart Button</label>
//                 </div>
                
//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="showCustomizeButton"
//                     className="mr-2"
//                     checked={content?.productPopupSettings?.showCustomizeButton !== false}
//                     onChange={(e) => updateContent('productPopupSettings', {
//                       ...(content?.productPopupSettings || {}),
//                       showCustomizeButton: e.target.checked
//                     })}
//                   />
//                   <label htmlFor="showCustomizeButton" className="text-sm">Customize Product Button</label>
//                 </div>
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Popup Size</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.productPopupSettings?.popupSize || 'medium'}
//                 onChange={(e) => updateContent('productPopupSettings', {
//                   ...(content?.productPopupSettings || {}),
//                   popupSize: e.target.value
//                 })}
//               >
//                 <option value="small">Small</option>
//                 <option value="medium">Medium</option>
//                 <option value="large">Large</option>
//                 <option value="fullscreen">Full Screen</option>
//               </select>
//             </div>
//           </div>
//         )}
//       </div>

      
//       {/* Product Grid Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <h4 className="font-medium text-base mb-3">Product Grid Configuration</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Products Per Row</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productsPerRow || 3}
//             onChange={(e) => updateContent('productsPerRow', parseInt(e.target.value, 10))}
//           >
//             <option value={1}>1 Product</option>
//             <option value={2}>2 Products</option>
//             <option value={3}>3 Products</option>
//             <option value={4}>4 Products</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product Card Style</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.productCardStyle || 'shadow'}
//             onChange={(e) => updateContent('productCardStyle', e.target.value)}
//           >
//             <option value="shadow">Shadow</option>
//             <option value="outline">Outline</option>
//             <option value="flat">Flat</option>
//           </select>
//         </div>
        
//         <div className="flex items-center mb-4">
//           <input
//             type="checkbox"
//             id="showQuickAction"
//             className="mr-2"
//             checked={content?.showQuickAction !== false}
//             onChange={(e) => updateContent('showQuickAction', e.target.checked)}
//           />
//           <label htmlFor="showQuickAction" className="text-sm font-medium">Show Quick Action Button</label>
//         </div>
        
//         {content?.showQuickAction !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Quick Action Icon</label>
//             <select
//               className="w-full p-2 border rounded"
//               value={content?.quickActionIcon || 'customize'}
//               onChange={(e) => updateContent('quickActionIcon', e.target.value)}
//             >
//               <option value="customize">Customize (Pencil)</option>
//               <option value="cart">Add to Cart</option>
//             </select>
//           </div>
//         )}
//       </div>
      
//       {/* API Configuration */}
//       <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//         <div className="flex justify-between items-start mb-3">
//           <h4 className="font-medium text-base">API Configuration</h4>
//           <div className="rounded-full bg-blue-100 p-1">
//             <Info size={16} className="text-blue-600" />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Category API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.categoryApiPath || '/api/categories'}
//             onChange={(e) => updateContent('categoryApiPath', e.target.value)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Product API Path</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.productApiPath || '/api/products/basic'}
//             onChange={(e) => updateContent('productApiPath', e.target.value)}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductCatalogForm;